import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { TermsOfService, PrivacyPolicy, Logo } from './components';

const API_URL = '';

function Auth({
  onAuthSuccess,
  initialMode = 'login',
  initialInfo = '',
  initialVerificationUrl = '',
  initialResetToken = ''
}) {
  const [mode, setMode] = useState(initialMode || 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState(initialResetToken || '');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const isLogin = mode === 'login';
  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';
  const isReset = mode === 'reset';

  useEffect(() => {
    if (initialMode) {
      setMode(initialMode);
    }
    if (initialInfo) {
      setInfo(initialInfo);
      setShowResend(false);
    }
    if (initialVerificationUrl) {
      setVerificationUrl(initialVerificationUrl);
    }
    setResetToken(initialResetToken || '');
  }, [initialMode, initialInfo, initialVerificationUrl, initialResetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setShowResend(false);
    setVerificationUrl('');

    if (isForgot) {
      if (!email) {
        setError('Email is required');
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to send reset link');
        }

        setInfo(data.message || 'If an account exists, a password reset link has been sent.');
        if (data.resetUrl) setVerificationUrl(data.resetUrl);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (isReset) {
      if (!resetToken) {
        setError('Reset link is invalid or missing. Please request a new one.');
        return;
      }

      if (!password || !confirmPassword) {
        setError('Password and confirmation are required');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token: resetToken, password })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to reset password');
        }

        setInfo(data.message || 'Password reset successful. You can now sign in.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        clearResetToken();
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Validation
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    if (isSignup && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (isSignup && !agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/signup';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.verificationRequired) {
          setInfo(data.message || 'Please verify your email to continue.');
          setShowResend(true);
          if (data.verificationUrl) setVerificationUrl(data.verificationUrl);
          return;
        }
        throw new Error(data.error || 'Authentication failed');
      }

      if (!data.token && data.verificationRequired) {
        setInfo(data.message || 'Check your email for a verification link.');
        setShowResend(true);
        if (data.verificationUrl) setVerificationUrl(data.verificationUrl);
        if (window.posthog && !isLogin) {
          window.posthog.capture('user_signed_up');
        }
        return;
      }

      // Store token and user info
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      // Track analytics
      if (window.posthog) {
        window.posthog.identify(data.user.email, {
          email: data.user.email,
          email_verified: data.user.email_verified || false,
          billing_status: data.user.billing_status || 'free'
        });
        window.posthog.capture(isLogin ? 'user_logged_in' : 'user_signed_up', {
          email_verified: data.user.email_verified || false
        });
      }

      // Call success callback
      onAuthSuccess(data.token, data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setError('');
    setInfo('');
    setVerificationUrl('');
    setIsResending(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      setInfo(data.message || 'Verification email sent.');
      if (data.verificationUrl) setVerificationUrl(data.verificationUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsResending(false);
    }
  };

  const resetAuthState = () => {
    setError('');
    setInfo('');
    setShowResend(false);
    setVerificationUrl('');
    setPassword('');
    setConfirmPassword('');
    setAgreedToTerms(false);
  };

  const clearResetToken = () => {
    setResetToken('');
    sessionStorage.removeItem('passwordResetToken');
    const url = new URL(window.location.href);
    if (url.searchParams.get('reset') === '1') {
      url.searchParams.delete('reset');
      window.history.replaceState({}, '', url.toString());
    }
  };

  const headerTitle = isLogin
    ? 'Welcome Back'
    : isSignup
    ? 'Create Account'
    : isForgot
    ? 'Reset Your Password'
    : 'Set a New Password';

  const headerSubtitle = isLogin
    ? 'Sign in to resmaker.ai'
    : isSignup
    ? 'Tailor your resume for each job you apply to'
    : isForgot
    ? 'We will email you a secure reset link.'
    : 'Choose a strong new password for your account.';

  const primaryLabel = isLogin
    ? 'Sign In'
    : isSignup
    ? 'Create Account'
    : isForgot
    ? 'Send Reset Link'
    : 'Update Password';

  const loadingLabel = isLogin
    ? 'Signing in...'
    : isSignup
    ? 'Creating account...'
    : isForgot
    ? 'Sending link...'
    : 'Updating password...';

  const infoLinkLabel = isForgot ? 'Open reset link' : 'Open verification link';
  const isPrimaryDisabled = isLoading || (isSignup && !agreedToTerms);

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-transparent">
      <div className="max-w-[460px] w-full bg-white/85 backdrop-blur-[12px] border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.08)] rounded-2xl p-12 animate-fadeIn">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-[72px] h-[72px] mx-auto mb-5 flex items-center justify-center">
            <Logo size={72} className="drop-shadow-lg" />
          </div>
          <h1 className="font-heading text-[32px] font-bold text-slate-900 mb-2 tracking-tight">
            {headerTitle}
          </h1>
          <p className="font-body text-[15px] text-slate-500">
            {headerSubtitle}
          </p>
        </div>

        {/* Error Alert - with icon and better visual hierarchy */}
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 mb-6 animate-shake">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center mt-0.5">
              <X size={12} className="text-white" />
            </div>
            <p className="text-red-700 m-0 text-sm font-body font-medium leading-relaxed">
              {error}
            </p>
          </div>
        )}

        {info && (
          <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 mb-6">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-600 flex items-center justify-center mt-0.5">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <p className="text-slate-700 m-0 text-sm font-body font-medium leading-relaxed">
                {info}
              </p>
              {verificationUrl && (
                <p className="mt-2 text-[13px] font-body">
                  <a href={verificationUrl} className="text-primary hover:text-dark transition-colors underline underline-offset-2">
                    {infoLinkLabel}
                  </a>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {!isReset && (
            <div className="mb-5">
              <label htmlFor="auth-email" className="block text-sm font-semibold text-slate-900 mb-2 font-body">
                Email Address
              </label>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full px-4 py-3.5 border border-slate-200 rounded-xl text-[15px] font-body bg-white/90 transition-all duration-150 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white hover:border-slate-300"
              />
            </div>
          )}

          {!isForgot && (
            <div className="mb-5">
              <label htmlFor="auth-password" className="block text-sm font-semibold text-slate-900 mb-2 font-body">
                {isReset ? 'New Password' : 'Password'}
              </label>
              <input
                id="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                className="w-full px-4 py-3.5 border border-slate-200 rounded-xl text-[15px] font-body bg-white/90 transition-all duration-150 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white hover:border-slate-300"
              />
              {(isSignup || isReset) && password.length > 0 && password.length < 6 && (
                <p className="mt-1.5 text-xs text-red-500 font-body">Password must be at least 6 characters</p>
              )}
            </div>
          )}

          {isLogin && (
            <div className="mb-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setMode('forgot');
                  resetAuthState();
                }}
                className="bg-transparent border-0 text-primary text-sm font-semibold font-body cursor-pointer no-underline transition-colors hover:text-secondary hover:underline"
              >
                Forgot password?
              </button>
            </div>
          )}

          {(isSignup || isReset) && (
            <div className="mb-5">
              <label htmlFor="auth-confirm-password" className="block text-sm font-semibold text-slate-900 mb-2 font-body">
                Confirm Password
              </label>
              <input
                id="auth-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                className={`w-full px-4 py-3.5 border rounded-xl text-[15px] font-body bg-white/90 transition-all duration-150 outline-none focus:ring-2 focus:bg-white hover:border-slate-300 ${
                  confirmPassword && confirmPassword !== password
                    ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                    : confirmPassword && confirmPassword === password
                    ? 'border-green-300 focus:border-green-400 focus:ring-green-100'
                    : 'border-slate-200 focus:border-primary focus:ring-primary/20'
                }`}
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="mt-1.5 text-xs text-red-600 font-body">Passwords do not match</p>
              )}
              {confirmPassword && confirmPassword === password && password.length >= 6 && (
                <p className="mt-1.5 text-xs text-green-600 font-body">Passwords match</p>
              )}
            </div>
          )}

          {isSignup && (
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer text-[13px] font-body text-slate-700 leading-relaxed group">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md border-2 transition-all duration-150 flex items-center justify-center ${
                    agreedToTerms
                      ? 'bg-primary border-primary'
                      : 'border-slate-300 group-hover:border-slate-400'
                  }`}>
                    {agreedToTerms && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </div>
                <span>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                    className="bg-transparent border-0 p-0 text-primary hover:text-dark underline underline-offset-2 font-semibold cursor-pointer [font:inherit] transition-colors"
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPrivacyModal(true);
                    }}
                    className="bg-transparent border-0 p-0 text-primary hover:text-dark underline underline-offset-2 font-semibold cursor-pointer [font:inherit] transition-colors"
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={isPrimaryDisabled}
            className={`w-full py-3 border-0 rounded-lg text-base font-medium font-body mb-5 transition-colors ${
              isPrimaryDisabled
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-primary text-white cursor-pointer hover:bg-dark'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {loadingLabel}
              </span>
            ) : (
              primaryLabel
            )}
          </button>

          {showResend && (isLogin || isSignup) && (
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={isResending}
              className={`w-full py-3 bg-transparent text-primary border-2 border-primary rounded-xl text-sm font-semibold font-body mb-5 transition-all ${
                isResending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-primary/10'
              }`}
            >
              {isResending ? 'Resending...' : 'Resend verification email'}
            </button>
          )}

          <div className="text-center">
            {isLogin && (
              <button
                type="button"
                onClick={() => {
                  setMode('signup');
                  resetAuthState();
                }}
                className="bg-transparent border-0 text-primary text-sm font-semibold font-body cursor-pointer no-underline transition-colors hover:text-secondary hover:underline"
              >
                Don&apos;t have an account? Sign up
              </button>
            )}
            {isSignup && (
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  resetAuthState();
                }}
                className="bg-transparent border-0 text-primary text-sm font-semibold font-body cursor-pointer no-underline transition-colors hover:text-secondary hover:underline"
              >
                Already have an account? Sign in
              </button>
            )}
            {(isForgot || isReset) && (
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  resetAuthState();
                  clearResetToken();
                }}
                className="bg-transparent border-0 text-primary text-sm font-semibold font-body cursor-pointer no-underline transition-colors hover:text-secondary hover:underline"
              >
                Back to sign in
              </button>
            )}
            {isReset && (
              <button
                type="button"
                onClick={() => {
                  setMode('forgot');
                  resetAuthState();
                  clearResetToken();
                }}
                className="mt-3 bg-transparent border-0 text-slate-500 text-sm font-semibold font-body cursor-pointer no-underline transition-colors hover:text-secondary hover:underline"
              >
                Need a new link? Request another
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5">
          <div className="bg-white rounded-2xl max-w-[900px] max-h-[80vh] w-full overflow-auto relative shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
            <button
              onClick={() => setShowTermsModal(false)}
              className="sticky top-4 left-full -ml-14 w-10 h-10 rounded-xl bg-primary text-white border-0 cursor-pointer flex items-center justify-center z-10 shadow-md"
              aria-label="Close terms"
            >
              <X size={20} />
            </button>
            <TermsOfService />
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5">
          <div className="bg-white rounded-2xl max-w-[900px] max-h-[80vh] w-full overflow-auto relative shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
            <button
              onClick={() => setShowPrivacyModal(false)}
              className="sticky top-4 left-full -ml-14 w-10 h-10 rounded-xl bg-primary text-white border-0 cursor-pointer flex items-center justify-center z-10 shadow-md"
              aria-label="Close privacy policy"
            >
              <X size={20} />
            </button>
            <PrivacyPolicy />
          </div>
        </div>
      )}
    </div>
  );
}

export default Auth;
